import React, { useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripeCardElement } from '@stripe/stripe-js';
import { sprintf } from 'sprintf';

import { SpinnerWithLabel } from 'components/Spinner';
import { formatPrice } from 'services/stringFormating';

import { ButtonSlideshow } from './ButtonSlideshow';

export type CreditCardFormStates = 'cc-waiting-user' | 'cc-submitting' | 'cc-success';

type Props = {
  state: CreditCardFormStates;
  priceCents: number;
  onSubmit: (cardElement: StripeCardElement) => void;
  onCancel: () => void;
};

export const CreditCardForm: React.FC<Props> = ({ state, priceCents, onSubmit, onCancel }) => {
  const [error, setError] = React.useState('');
  const stripe = useStripe();
  const elements = useElements();

  const handleCreditCardSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);

    if (cardElement) {
      onSubmit(cardElement);
    }
  };

  useEffect(() => {
    if (elements) {
      const cardElement = elements.getElement(CardElement);
      cardElement?.on('change', (event) => {
        setError(event.error ? event.error.message : '');
      });
      cardElement?.focus();
    }
  }, [elements]);

  return (
    <form className='mb-0' onSubmit={handleCreditCardSubmit}>
      <div className='mb-2'>
        <p className='mb-1'>
          {sprintf(
            'Enter your credit card details to submit a bid. When you place a bid we will reserve %s on your credit card. Your funds will be released if you are outbid.',
            formatPrice(priceCents, 'USD'),
          )}
        </p>
        <div className='mb-3'>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  fontFamily: 'Suisse',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
          {error.length > 0 ? <div className='mt-1 text-error'>{error}</div> : null}
        </div>
      </div>

      <ButtonSlideshow state={state}>
        {state === 'cc-waiting-user' ? (
          <>
            <button type='submit' className='btn bg-black'>
              {priceCents ? sprintf('Bid %s', formatPrice(priceCents, 'USD')) : 'Bid'}
            </button>
            <button className='btn btn--blended' onClick={onCancel}>
              Cancel
            </button>
          </>
        ) : null}
        {state === 'cc-submitting' ? (
          <SpinnerWithLabel>Submitting your bid...</SpinnerWithLabel>
        ) : null}
        {state === 'cc-success' ? (
          <div className='key-value key-value__value'>
            <span className='icon-checkmark text-success mr-mini' />
            Success
          </div>
        ) : null}
      </ButtonSlideshow>
    </form>
  );
};
